import { SortDirection } from '@atlas/ui-components'
import type { AdminConcernRow } from '@/services/file.service'

/** One file and every concern row recorded against it. */
export interface FileConcernGroup {
	fileId: string
	fileName: string | null
	contentType: string | null
	thumbnailUrl?: string
	fileDeleted: boolean
	concerns: AdminConcernRow[]
	/** True when any concern is stale — the file is what an operator scans for. */
	hasStale: boolean
	/** True when any concern failed. */
	hasFailed: boolean
	/** The most recent `createdAt` across the group, for ordering. */
	latestAt: string
}

/** Columns an operator can order the table by. */
export type SortKey = 'file' | 'concerns' | 'status' | 'latest'
export { SortDirection }

/** Worst-first, so ordering by status surfaces what needs attention. */
const STATUS_SEVERITY = (group: FileConcernGroup): number => {
	if (group.hasFailed) return 0
	if (group.hasStale) return 1
	return 2
}

/**
 * Order the grouped rows.
 *
 * Sorts GROUPS, never the concerns inside one: a file's concerns are a fixed
 * small set, and reordering them per column would make the same file read
 * differently between sorts.
 */
export function sortGroups(
	groups: readonly FileConcernGroup[],
	key: SortKey,
	direction: SortDirection,
): FileConcernGroup[] {
	const sign = direction === SortDirection.ASC ? 1 : -1
	return [...groups].sort((a, b) => {
		switch (key) {
			case 'file':
				return sign * (a.fileName ?? a.fileId).localeCompare(b.fileName ?? b.fileId)
			case 'concerns':
				return sign * (a.concerns.length - b.concerns.length)
			case 'status':
				return sign * (STATUS_SEVERITY(a) - STATUS_SEVERITY(b))
			default:
				return sign * a.latestAt.localeCompare(b.latestAt)
		}
	})
}

/**
 * Collapse a flat concern list into one node per file.
 *
 * The endpoint returns a row per (file, concern), so a four-concern upload
 * occupies four rows that repeat the same file id and name — the table grew by
 * four per upload and the thing an operator is actually looking for (which FILE
 * is stuck) had to be reassembled by eye.
 *
 * File order follows the newest concern in each group, matching the flat list's
 * newest-first ordering; within a group, concerns keep the order the server sent
 * so a caller's sort is not silently overridden.
 */
export function groupConcernsByFile(rows: readonly AdminConcernRow[]): FileConcernGroup[] {
	const groups = new Map<string, FileConcernGroup>()

	for (const row of rows) {
		const existing = groups.get(row.fileId)
		if (!existing) {
			groups.set(row.fileId, {
				fileId: row.fileId,
				fileName: row.fileName,
				contentType: row.contentType,
				thumbnailUrl: row.thumbnailUrl,
				fileDeleted: row.fileDeleted,
				concerns: [row],
				hasStale: row.stale,
				hasFailed: row.status === 'failed',
				latestAt: row.createdAt,
			})
			continue
		}
		existing.concerns.push(row)
		existing.hasStale ||= row.stale
		existing.hasFailed ||= row.status === 'failed'
		if (row.createdAt > existing.latestAt) existing.latestAt = row.createdAt
	}

	return [...groups.values()].sort((a, b) => b.latestAt.localeCompare(a.latestAt))
}
